import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  addResearchBrandSource,
  createResearchBrand,
  deleteResearchBrand,
  deleteResearchBrandSource,
  listResearchBrands,
  unlinkResearchBrandCustomer,
} from "../../api/Skills/skillAgentResearchBrandsApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

/**
 * Gestionar catálogo Marca+Web del cliente (BBDD).
 */
export default function SkillAgentResearchCatalogDialog({
  open,
  onClose,
  customerId,
  customerName,
  onChanged,
}) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftUrl, setDraftUrl] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [expandId, setExpandId] = useState(null);
  const [sourceDraft, setSourceDraft] = useState({ url: "", label: "" });

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listResearchBrands({ customerId });
      setBrands(Array.isArray(data?.brands) ? data.brands : []);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar el catálogo.");
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleCreate = async () => {
    const name = draftName.trim();
    const url = draftUrl.trim();
    if (!name) {
      setError("Nombre de marca obligatorio.");
      return;
    }
    if (url && !/^https?:\/\//i.test(url)) {
      setError("La URL debe empezar por http:// o https://");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await createResearchBrand({
        name,
        customerId: Number(customerId),
        sources: url
          ? [{ url, label: draftLabel.trim() || null, defaultSelected: true }]
          : [],
      });
      setDraftName("");
      setDraftUrl("");
      setDraftLabel("");
      setOkMsg("Marca añadida al cliente.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo crear.");
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async (brand) => {
    if (!brand?.dbId) return;
    setBusy(true);
    setError("");
    try {
      await unlinkResearchBrandCustomer(brand.dbId, Number(customerId));
      // Si solo estaba en este cliente, soft-delete opcional no; solo desligar.
      setOkMsg(`«${brand.name}» desligada de este cliente.`);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo desligar.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteBrand = async (brand) => {
    if (!brand?.dbId) return;
    if (
      !window.confirm(
        `¿Borrar la marca «${brand.name}» del catálogo global? Si otros clientes la usan, también la perderán.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await deleteResearchBrand(brand.dbId);
      setOkMsg("Marca borrada.");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo borrar.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddSource = async (brand) => {
    const url = sourceDraft.url.trim();
    if (!brand?.dbId || !url) return;
    if (!/^https?:\/\//i.test(url)) {
      setError("La URL debe empezar por http:// o https://");
      return;
    }
    setBusy(true);
    try {
      await addResearchBrandSource(brand.dbId, {
        url,
        label: sourceDraft.label.trim() || null,
        defaultSelected: true,
      });
      setSourceDraft({ url: "", label: "" });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo añadir la web.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSource = async (brand, source) => {
    if (!brand?.dbId || !source?.id) {
      setError("Esta web aún no tiene id en BBDD; recarga el catálogo.");
      return;
    }
    setBusy(true);
    try {
      await deleteResearchBrandSource(brand.dbId, source.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo borrar la web.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={800}>
        Catálogo de marcas · {customerName || `Cliente #${customerId}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Marcas y webs en base de datos. Puedes añadir, quitar del cliente o
            borrar. La misma marca se puede ligar a varios clientes.
          </Typography>
          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          {okMsg && (
            <Alert severity="success" onClose={() => setOkMsg("")}>
              {okMsg}
            </Alert>
          )}

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1.25,
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Nueva marca
            </Typography>
            <Stack spacing={1}>
              <TextField
                size="small"
                label="Nombre"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                fullWidth
                sx={pageFieldSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  label="URL (opcional)"
                  value={draftUrl}
                  onChange={(e) => setDraftUrl(e.target.value)}
                  fullWidth
                  sx={pageFieldSx}
                />
                <TextField
                  size="small"
                  label="Label"
                  value={draftLabel}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  sx={{ width: { xs: "100%", sm: 140 }, ...pageFieldSx }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={busy || !draftName.trim()}
                  onClick={handleCreate}
                  sx={{ ...pagePrimaryButtonSx, textTransform: "none" }}
                >
                  Añadir
                </Button>
              </Stack>
            </Stack>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Stack spacing={0.75} sx={{ maxHeight: 360, overflow: "auto" }}>
              {brands.map((b) => (
                <Box
                  key={b.id || b.dbId}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.75,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {b.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(b.sources || []).length} web
                        {(b.sources || []).length === 1 ? "" : "s"} · {b.id}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        sx={{ textTransform: "none" }}
                        onClick={() =>
                          setExpandId(expandId === b.dbId ? null : b.dbId)
                        }
                      >
                        Webs
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        sx={{ textTransform: "none" }}
                        disabled={busy || !b.dbId}
                        onClick={() => handleUnlink(b)}
                      >
                        Quitar del cliente
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={busy || !b.dbId}
                        onClick={() => handleDeleteBrand(b)}
                        aria-label="Borrar marca"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {expandId === b.dbId && (
                    <Box sx={{ mt: 1, pl: 0.5 }}>
                      {(b.sources || []).map((s) => (
                        <Stack
                          key={s.id || s.url}
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ mb: 0.35 }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ wordBreak: "break-all", flex: 1 }}
                          >
                            {s.defaultSelected ? "● " : "○ "}
                            {s.label || "URL"} — {s.url}
                          </Typography>
                          {s.id ? (
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => handleDeleteSource(b, s)}
                              aria-label="Quitar web"
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          ) : null}
                        </Stack>
                      ))}
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={0.75}
                        sx={{ mt: 1 }}
                      >
                        <TextField
                          size="small"
                          label="Nueva URL"
                          value={sourceDraft.url}
                          onChange={(e) =>
                            setSourceDraft((p) => ({
                              ...p,
                              url: e.target.value,
                            }))
                          }
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Label"
                          value={sourceDraft.label}
                          onChange={(e) =>
                            setSourceDraft((p) => ({
                              ...p,
                              label: e.target.value,
                            }))
                          }
                          sx={{ width: { xs: "100%", sm: 120 } }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy || !sourceDraft.url.trim()}
                          onClick={() => handleAddSource(b)}
                          sx={{ textTransform: "none" }}
                        >
                          + Web
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Box>
              ))}
              {!brands.length && (
                <Typography variant="caption" color="text.secondary">
                  Sin marcas ligadas a este cliente.
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
